import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Home,
  Truck,
  ShieldCheck,
  Check,
  MapPin,
  Package,
  Loader2,
  AlertCircle,
  Banknote,
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { formatPrice } from '../lib/format';
import { COMPANY } from '../lib/company';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, totalPrice, clearCart } = useCart();
  const [counties, setCounties] = useState([]);
  const [cities, setCities] = useState([]);
  const [lockers, setLockers] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingLockers, setLoadingLockers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    countyId: '',
    countyName: '',
    cityName: '',
    postal: '',
    notes: '',
    deliveryMethod: 'home',
    lockerId: '',
    lockerName: '',
  });

  const shipping = totalPrice >= 500 ? 0 : 25;
  const grandTotal = totalPrice + shipping;

  // Load counties on mount
  useEffect(() => {
    axios.get(`${API}/sameday/counties`)
      .then((r) => setCounties(r.data))
      .catch(() => setCounties([]));
  }, []);

  // Load cities when county changes
  useEffect(() => {
    if (!form.countyId) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    axios.get(`${API}/sameday/cities`, { params: { county: form.countyId } })
      .then((r) => setCities(r.data))
      .catch(() => setCities([]))
      .finally(() => setLoadingCities(false));
  }, [form.countyId]);

  // Load lockers when delivery method = locker
  useEffect(() => {
    if (form.deliveryMethod !== 'locker' || !form.countyId) return;
    setLoadingLockers(true);
    axios.get(`${API}/sameday/lockers`, { params: { county: form.countyId } })
      .then((r) => setLockers(r.data || []))
      .catch(() => setLockers([]))
      .finally(() => setLoadingLockers(false));
  }, [form.deliveryMethod, form.countyId]);

  const handleCountyChange = (e) => {
    const id = e.target.value;
    const c = counties.find((x) => String(x.id) === id);
    setForm({ ...form, countyId: id, countyName: c?.name || '', cityName: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.deliveryMethod === 'locker' && !form.lockerId) {
      setError('Vă rugăm selectați un locker Easybox.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.deliveryMethod === 'locker' ? (form.lockerName || form.address) : form.address,
        city: form.cityName,
        county: form.countyName,
        postal_code: form.postal || '',
        notes: form.notes || '',
        delivery_method: form.deliveryMethod,
        locker_id: form.deliveryMethod === 'locker' ? form.lockerId : null,
        items: cart.map((i) => ({
          id: i.id, name: i.name, price: i.price, qty: i.qty,
        })),
        subtotal: totalPrice,
        shipping,
        total: grandTotal,
      };
      const r = await axios.post(`${API}/orders`, payload);
      setResult(r.data);
      clearCart();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'A apărut o eroare la plasarea comenzii. Vă rugăm reîncercați sau ne contactați la ' + COMPANY.phone;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Success state
  if (result) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
          <Check className="w-10 h-10 text-emerald-600" />
        </div>
        <h1 className="mt-6 font-serif text-3xl md:text-4xl font-bold text-neutral-900">
          Comandă plasată cu succes!
        </h1>
        <p className="mt-3 text-neutral-600">
          Mulțumim pentru comanda ta. Ți-am trimis confirmarea pe email.
        </p>

        <div className="mt-8 bg-white border border-neutral-200 rounded-2xl p-6 text-left space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-neutral-500">Număr comandă</span>
            <span className="font-mono font-semibold text-neutral-900">{result.order_number}</span>
          </div>
          {result.awb_number && (
            <div className="flex justify-between text-sm">
              <span className="text-neutral-500">AWB Sameday</span>
              <span className="font-mono font-semibold text-amber-700">{result.awb_number}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-neutral-500">Total de plată la livrare</span>
            <span className="font-bold text-lg text-neutral-900">{formatPrice(result.total)}</span>
          </div>
          {result.estimated_delivery && (
            <div className="flex justify-between text-sm">
              <span className="text-neutral-500">Livrare estimată</span>
              <span className="font-semibold text-neutral-900">{result.estimated_delivery}</span>
            </div>
          )}
          <div className="pt-3 mt-3 border-t border-neutral-100 text-xs text-neutral-500">
            {result.awb_number ? (
              <>
                <p className="flex items-start gap-1.5">
                  <Truck className="w-3.5 h-3.5 mt-0.5 text-amber-600 shrink-0" />
                  Pachetul tău va fi preluat de Sameday și livrat în 24-48h. Vei primi notificări pe SMS/email.
                </p>
              </>
            ) : (
              <p className="flex items-start gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 text-amber-600 shrink-0" />
                Comanda a fost înregistrată. Un agent te va contacta pentru confirmare.
              </p>
            )}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to="/" className="inline-block px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-full font-medium">
            Înapoi la pagina principală
          </Link>
          <Link to="/magazin" className="inline-block px-6 py-3 bg-white border border-neutral-300 hover:border-neutral-900 text-neutral-900 rounded-full font-medium">
            Continuă cumpărăturile
          </Link>
        </div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-neutral-900">Coșul tău este gol</h1>
        <Link to="/magazin" className="mt-6 inline-block px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-full">
          Mergi la magazin
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <div className="bg-neutral-50 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-3 text-sm flex items-center gap-2 text-neutral-500">
          <Link to="/" className="hover:text-amber-600 flex items-center gap-1">
            <Home className="w-3.5 h-3.5" /> Acasă
          </Link>
          <span>/</span>
          <Link to="/cos" className="hover:text-amber-600">Coș</Link>
          <span>/</span>
          <span className="text-neutral-900">Finalizare comandă</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">
        <h1 className="font-serif text-3xl md:text-4xl font-bold text-neutral-900 mb-2">
          Finalizare comandă
        </h1>
        <p className="text-neutral-500 mb-8">Livrare Sameday · Plata se face ramburs la curier</p>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 flex items-start gap-2 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Detalii client */}
            <section className="bg-white border border-neutral-200 rounded-2xl p-6">
              <h2 className="font-serif font-bold text-xl text-neutral-900 mb-4">Datele tale</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-neutral-700 mb-1">Nume complet *</label>
                  <input required type="text" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border border-neutral-300 focus:border-amber-500 outline-none px-4 py-2.5 rounded-lg text-sm"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-1">Email *</label>
                  <input required type="email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full border border-neutral-300 focus:border-amber-500 outline-none px-4 py-2.5 rounded-lg text-sm"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-1">Telefon *</label>
                  <input required type="tel" placeholder="07XX XXX XXX" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full border border-neutral-300 focus:border-amber-500 outline-none px-4 py-2.5 rounded-lg text-sm"/>
                </div>
              </div>
            </section>

            {/* Livrare */}
            <section className="bg-white border border-neutral-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Truck className="w-5 h-5 text-amber-600" />
                <h2 className="font-serif font-bold text-xl text-neutral-900">Livrare Sameday</h2>
              </div>

              {/* Metoda de livrare */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                {[
                  { id: 'home', icon: Home, label: 'La domiciliu', desc: 'Curier până la adresa ta' },
                  { id: 'locker', icon: Package, label: 'Easybox Locker', desc: 'Ridicare de la un locker' },
                ].map((opt) => (
                  <label
                    key={opt.id}
                    className={`flex items-start gap-3 border rounded-xl p-4 cursor-pointer transition-colors ${
                      form.deliveryMethod === opt.id
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-neutral-200 hover:border-neutral-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="deliveryMethod"
                      value={opt.id}
                      checked={form.deliveryMethod === opt.id}
                      onChange={() => setForm({ ...form, deliveryMethod: opt.id, lockerId: '', lockerName: '' })}
                      className="mt-1 accent-amber-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5">
                        <opt.icon className="w-4 h-4 text-neutral-700" />
                        <span className="font-medium text-neutral-900 text-sm">{opt.label}</span>
                      </div>
                      <div className="text-xs text-neutral-500 mt-0.5">{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Județ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-1">Județ *</label>
                  <select required value={form.countyId} onChange={handleCountyChange}
                    className="w-full border border-neutral-300 focus:border-amber-500 outline-none px-4 py-2.5 rounded-lg text-sm bg-white cursor-pointer">
                    <option value="">— Alege județ —</option>
                    {counties.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-1">Oraș *</label>
                  <select required value={form.cityName}
                    onChange={(e) => setForm({ ...form, cityName: e.target.value })}
                    disabled={!form.countyId || loadingCities}
                    className="w-full border border-neutral-300 focus:border-amber-500 outline-none px-4 py-2.5 rounded-lg text-sm bg-white cursor-pointer disabled:bg-neutral-50 disabled:cursor-not-allowed">
                    <option value="">{loadingCities ? 'Se încarcă...' : '— Alege oraș —'}</option>
                    {cities.map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Adresă pentru home delivery */}
              {form.deliveryMethod === 'home' && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Adresă (stradă, nr., bloc, ap.) *</label>
                    <input required type="text" value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      className="w-full border border-neutral-300 focus:border-amber-500 outline-none px-4 py-2.5 rounded-lg text-sm"/>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Cod poștal</label>
                    <input type="text" value={form.postal}
                      onChange={(e) => setForm({ ...form, postal: e.target.value })}
                      className="w-full border border-neutral-300 focus:border-amber-500 outline-none px-4 py-2.5 rounded-lg text-sm"/>
                  </div>
                </div>
              )}

              {/* Selector locker */}
              {form.deliveryMethod === 'locker' && (
                <div className="mt-4">
                  <label className="block text-xs font-medium text-neutral-700 mb-1">
                    Easybox Locker {form.countyId && `(${lockers.length} disponibile)`} *
                  </label>
                  {!form.countyId ? (
                    <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 text-sm text-neutral-500">
                      Selectează județul pentru a vedea lockerele disponibile.
                    </div>
                  ) : loadingLockers ? (
                    <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 text-sm text-neutral-500 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Se încarcă lockerele...
                    </div>
                  ) : lockers.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                      Nu am găsit lockere pentru acest județ. Te rugăm să alegi livrare la domiciliu.
                    </div>
                  ) : (
                    <div className="max-h-72 overflow-y-auto border border-neutral-200 rounded-lg divide-y divide-neutral-100">
                      {lockers.map((l) => (
                        <label
                          key={l.id}
                          className={`flex items-start gap-3 p-3 cursor-pointer transition-colors ${
                            form.lockerId === String(l.id) ? 'bg-amber-50' : 'hover:bg-neutral-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="locker"
                            value={l.id}
                            checked={form.lockerId === String(l.id)}
                            onChange={() => setForm({
                              ...form,
                              lockerId: String(l.id),
                              lockerName: `${l.name} — ${l.address}`,
                            })}
                            className="mt-1 accent-amber-500"
                          />
                          <MapPin className="w-4 h-4 text-amber-600 mt-1 shrink-0" />
                          <div className="flex-1">
                            <div className="font-medium text-sm text-neutral-900">{l.name}</div>
                            <div className="text-xs text-neutral-500 mt-0.5">{l.address}</div>
                            <div className="text-xs text-neutral-400 mt-0.5">{l.city}{l.postalCode ? `, ${l.postalCode}` : ''}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-5">
                <label className="block text-xs font-medium text-neutral-700 mb-1">Note comandă (opțional)</label>
                <textarea rows="2" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full border border-neutral-300 focus:border-amber-500 outline-none px-4 py-2.5 rounded-lg text-sm"/>
              </div>
            </section>

            {/* Plată */}
            <section className="bg-white border border-neutral-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Banknote className="w-5 h-5 text-amber-600" />
                <h2 className="font-serif font-bold text-xl text-neutral-900">Metoda de plată</h2>
              </div>
              <div className="border border-amber-500 bg-amber-50 rounded-xl p-4 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0">
                  <Banknote className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold text-neutral-900">Ramburs la livrare</div>
                  <div className="text-sm text-neutral-600 mt-0.5">
                    Plătești curierului Sameday la primirea coletului. Singura metodă disponibilă.
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Sumar */}
          <aside className="bg-white border border-neutral-200 rounded-2xl p-6 h-fit lg:sticky lg:top-32">
            <h3 className="font-serif font-bold text-xl text-neutral-900">Comanda ta</h3>
            <ul className="mt-5 space-y-3 max-h-64 overflow-y-auto pr-1">
              {cart.map((item) => (
                <li key={item.id} className="flex gap-3 text-sm">
                  <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded-lg bg-neutral-50 shrink-0" />
                  <div className="flex-1">
                    <div className="text-neutral-800 line-clamp-2 leading-snug">{item.name}</div>
                    <div className="text-xs text-neutral-500">× {item.qty}</div>
                  </div>
                  <span className="font-medium shrink-0">{formatPrice(item.price * item.qty)}</span>
                </li>
              ))}
            </ul>
            <dl className="mt-5 pt-4 border-t border-neutral-200 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-neutral-500">Subtotal</dt><dd>{formatPrice(totalPrice)}</dd></div>
              <div className="flex justify-between"><dt className="text-neutral-500">Livrare Sameday</dt><dd>{shipping === 0 ? 'Gratuit' : formatPrice(shipping)}</dd></div>
              <div className="flex justify-between text-lg pt-2 border-t border-neutral-100">
                <dt className="font-semibold">Total ramburs</dt>
                <dd className="font-bold">{formatPrice(grandTotal)}</dd>
              </div>
            </dl>
            <button
              type="submit"
              disabled={submitting}
              className="mt-6 w-full bg-amber-500 hover:bg-amber-600 disabled:bg-neutral-400 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-full transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Se procesează...</>
              ) : (
                <>Plasează comanda</>
              )}
            </button>
            <p className="text-xs text-neutral-500 mt-3 text-center">
              Prin plasarea comenzii ești de acord cu{' '}
              <Link to="/termeni-si-conditii" className="text-amber-700 underline">Termenii și Condițiile</Link>.
            </p>
            <div className="mt-5 grid grid-cols-3 gap-2 pt-4 border-t border-neutral-200">
              <div className="flex flex-col items-center gap-1 text-center">
                <Truck className="w-4 h-4 text-amber-600" />
                <span className="text-[10px] text-neutral-600">Livrare 24-48h</span>
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <ShieldCheck className="w-4 h-4 text-amber-600" />
                <span className="text-[10px] text-neutral-600">Curier Sameday</span>
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <Banknote className="w-4 h-4 text-amber-600" />
                <span className="text-[10px] text-neutral-600">Ramburs</span>
              </div>
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
